import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { accountAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';

const memberLabel = (membership) => {
  const name = `${membership.user?.firstName || ''} ${membership.user?.lastName || ''}`.trim();
  return name || membership.invitedEmail;
};

function AccountSharingPage() {
  const { sharedAccounts, refreshAccounts, activeAccount, switchAccount } = useAuth();
  const [email, setEmail] = useState('');
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      setMembers(await accountAPI.getMembers());
    } catch (error) {
      toast.error(error.response?.data?.error || 'Could not load account sharing');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let active = true;
    setLoading(true);
    accountAPI.getMembers()
      .then((result) => { if (active) setMembers(result); })
      .catch((error) => { if (active) toast.error(error.response?.data?.error || 'Could not load account sharing'); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, []);

  const invite = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    try {
      await accountAPI.invite(email);
      setEmail('');
      toast.success('Invitation sent');
      await load();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Could not send invitation');
    } finally {
      setSubmitting(false);
    }
  };

  const revoke = async (membership) => {
    if (!window.confirm(`Remove access for ${memberLabel(membership)}?`)) return;
    await accountAPI.revoke(membership.id);
    toast.success('Access removed');
    await load();
  };

  const leave = async (account) => {
    if (!window.confirm(`Leave ${account.name}?`)) return;
    await accountAPI.leave(account.id);
    if (activeAccount?.id === account.id) {
      const personal = (await refreshAccounts()).find((candidate) => candidate.isPersonal);
      if (personal) switchAccount(personal.id);
    } else {
      await refreshAccounts();
    }
    toast.success('You left the shared account');
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Account sharing</h1>
        <p className="mt-1 text-sm text-gray-600">Invite people to collaborate, or manage accounts shared with you.</p>
      </div>

      <section className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
        <h2 className="font-semibold text-gray-900">Invite someone</h2>
        <p className="mt-1 text-sm text-gray-600">Members can view and edit all financial data in your account.</p>
        <form onSubmit={invite} className="mt-4 flex flex-col gap-3 sm:flex-row">
          <input
            type="email"
            required
            maxLength={320}
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            placeholder="person@example.com"
            className="flex-1 rounded-xl border border-gray-300 px-3 py-2"
          />
          <button disabled={submitting} className="rounded-xl bg-blue-600 px-4 py-2 font-medium text-white disabled:opacity-50">
            {submitting ? 'Sending…' : 'Send invitation'}
          </button>
        </form>
      </section>

      <section className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
        <h2 className="font-semibold text-gray-900">People with access</h2>
        {loading ? <p className="mt-3 text-sm text-gray-500">Loading…</p> : (
          <div className="mt-3 divide-y divide-gray-100">
            {!members.length && <p className="py-3 text-sm text-gray-500">No invitations or members yet.</p>}
            {members.map((membership) => (
              <div key={membership.id} className="flex items-center justify-between gap-3 py-3">
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium text-gray-900">{memberLabel(membership)}</p>
                  <p className="text-xs text-gray-500">{membership.status === 'PENDING' ? 'Invitation pending' : 'Full access member'}</p>
                </div>
                <div className="flex gap-2">
                  {membership.status === 'PENDING' && (
                    <button onClick={() => { setEmail(membership.invitedEmail); }} className="text-sm font-medium text-blue-600">
                      Use email
                    </button>
                  )}
                  <button onClick={() => revoke(membership)} className="text-sm font-medium text-red-600">Revoke</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
        <h2 className="font-semibold text-gray-900">Shared with me</h2>
        <div className="mt-3 divide-y divide-gray-100">
          {!sharedAccounts.length && <p className="py-3 text-sm text-gray-500">No accounts are shared with you.</p>}
          {sharedAccounts.map((account) => (
            <div key={account.id} className="flex items-center justify-between gap-3 py-3">
              <div>
                <p className="text-sm font-medium text-gray-900">{account.name}</p>
                <p className="text-xs text-gray-500">{account.role === 'VIEWER' ? 'Read only' : 'Full access'}</p>
              </div>
              <button onClick={() => leave(account)} className="text-sm font-medium text-red-600">Leave</button>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

export default AccountSharingPage;
